import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiGet } from "../lib/api";
import { COLORS } from "../lib/colors";

function formatTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const EVENT_LABELS = {
  deactivated: { label: "Deactivated", color: COLORS.red, bg: COLORS.redLight },
  reactivated: { label: "Reactivated", color: COLORS.green, bg: COLORS.greenLight },
  role_changed: { label: "Role Changed", color: COLORS.purple, bg: COLORS.purpleLight },
  school_changed: { label: "School Changed", color: COLORS.orange, bg: COLORS.orangeLight },
};

const EVENT_FILTERS = [
  { key: "all", label: "All Events" },
  { key: "deactivated", label: "Deactivations" },
  { key: "reactivated", label: "Reactivations" },
];

const DATE_FILTERS = [
  { key: "all", label: "All Time" },
  { key: "7", label: "Last 7 Days" },
  { key: "30", label: "Last 30 Days" },
  { key: "90", label: "Last 90 Days" },
];

export default function EventsLog() {
  const { session, schoolId, isPlatformAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Platform admin filters
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const [dateRange, setDateRange] = useState("all");

  const PAGE_SIZE = 50;

  // Load schools list for platform admins
  useEffect(() => {
    if (!isPlatformAdmin || !session?.access_token) return;
    apiGet("/schools", session.access_token)
      .then((data) => setSchools(data.schools || []))
      .catch((err) => console.error("Failed to load schools:", err));
  }, [isPlatformAdmin, session?.access_token]);

  const loadEvents = useCallback(async (pageNum = 0) => {
    if (!session?.access_token) return;
    setLoading(true);
    try {
      let path = `/dashboard/user-events?limit=${PAGE_SIZE}&offset=${pageNum * PAGE_SIZE}`;
      if (filter !== "all") path += `&event_type=${filter}`;
      if (isPlatformAdmin && selectedSchool) path += `&school_id=${selectedSchool}`;

      const data = await apiGet(path, session.access_token);
      let eventList = data.events || [];

      // Client-side filters
      if (emailSearch.trim()) {
        const q = emailSearch.trim().toLowerCase();
        eventList = eventList.filter((evt) =>
          (evt.metadata?.target_email || "").toLowerCase().includes(q) ||
          (evt.performed_by_email || "").toLowerCase().includes(q)
        );
      }
      if (dateRange !== "all") {
        const days = parseInt(dateRange, 10);
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        eventList = eventList.filter((evt) => new Date(evt.created_at) >= cutoff);
      }

      setEvents(eventList);
      setHasMore((data.events || []).length >= PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load events:", err);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, filter, isPlatformAdmin, selectedSchool, emailSearch, dateRange]);

  useEffect(() => {
    setPage(0);
    loadEvents(0);
  }, [loadEvents]);

  const goPage = (newPage) => {
    setPage(newPage);
    loadEvents(newPage);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, margin: "0 0 6px" }}>
          User Events Log
        </h2>
        <p style={{ fontSize: 13, color: COLORS.textSecondary, margin: 0 }}>
          Audit trail of all user lifecycle changes{isPlatformAdmin ? " across the platform" : ""}.
        </p>
      </div>

      {/* Event type filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {EVENT_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
              fontFamily: "inherit", cursor: "pointer", transition: "all 0.15s",
              border: `1px solid ${filter === f.key ? COLORS.accent : COLORS.border}`,
              backgroundColor: filter === f.key ? COLORS.accent : "transparent",
              color: filter === f.key ? "#fff" : COLORS.textSecondary,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Platform admin filters */}
      {isPlatformAdmin && (
        <div style={{
          display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center",
        }}>
          {/* School filter */}
          <select
            value={selectedSchool}
            onChange={(e) => setSelectedSchool(e.target.value)}
            style={{
              padding: "7px 12px", borderRadius: 8, fontSize: 13,
              border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface,
              color: COLORS.text, fontFamily: "inherit", cursor: "pointer",
              minWidth: 180,
            }}
          >
            <option value="">All Schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          {/* Email search */}
          <input
            type="text"
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
            placeholder="Filter by email..."
            style={{
              padding: "7px 12px", borderRadius: 8, fontSize: 13,
              border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface,
              color: COLORS.text, fontFamily: "inherit", outline: "none",
              minWidth: 200,
            }}
          />

          {/* Date range */}
          <div style={{ display: "flex", gap: 4 }}>
            {DATE_FILTERS.map((d) => (
              <button
                key={d.key}
                onClick={() => setDateRange(d.key)}
                style={{
                  padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500,
                  fontFamily: "inherit", cursor: "pointer",
                  border: `1px solid ${dateRange === d.key ? COLORS.accent : COLORS.border}`,
                  backgroundColor: dateRange === d.key ? COLORS.accent : "transparent",
                  color: dateRange === d.key ? "#fff" : COLORS.textSecondary,
                }}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{
        backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 12, overflow: "hidden",
      }}>
        {/* Table Header */}
        <div style={{
          display: "grid", gridTemplateColumns: "180px 120px 1fr 1fr 1fr 1fr",
          gap: 0, padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.surfaceAlt,
        }}>
          {["Timestamp", "Event", "User", "Email", "Performed By", "Reason"].map((h) => (
            <span key={h} style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {h}
            </span>
          ))}
        </div>

        {/* Rows */}
        {loading ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <p style={{ fontSize: 13, color: COLORS.textMuted }}>Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <p style={{ fontSize: 13, color: COLORS.textSecondary }}>No events found.</p>
          </div>
        ) : (
          events.map((evt) => {
            const style = EVENT_LABELS[evt.event_type] || { label: evt.event_type, color: COLORS.textMuted, bg: COLORS.surfaceAlt };
            const meta = evt.metadata || {};
            return (
              <div
                key={evt.id}
                style={{
                  display: "grid", gridTemplateColumns: "180px 120px 1fr 1fr 1fr 1fr",
                  gap: 0, padding: "14px 20px",
                  borderBottom: `1px solid ${COLORS.borderLight}`,
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 12, color: COLORS.textSecondary }}>
                  {formatTime(evt.created_at)}
                </span>
                <span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, color: style.color,
                    backgroundColor: style.bg, padding: "3px 8px", borderRadius: 4,
                  }}>
                    {style.label}
                  </span>
                </span>
                <span style={{ fontSize: 13, color: COLORS.text, fontWeight: 500 }}>
                  {meta.target_role || "\u2014"}
                </span>
                <span style={{ fontSize: 12, color: COLORS.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {meta.target_email || "\u2014"}
                </span>
                <span style={{ fontSize: 12, color: COLORS.textSecondary }}>
                  {evt.performed_by_email || "System"}
                </span>
                <span style={{ fontSize: 12, color: COLORS.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {evt.reason || "\u2014"}
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {(page > 0 || hasMore) && (
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 20 }}>
          <button
            onClick={() => goPage(page - 1)}
            disabled={page === 0}
            style={{
              padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500,
              fontFamily: "inherit", cursor: page === 0 ? "default" : "pointer",
              border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.surface, color: page === 0 ? COLORS.textMuted : COLORS.text,
              opacity: page === 0 ? 0.5 : 1,
            }}
          >
            Previous
          </button>
          <span style={{ fontSize: 13, color: COLORS.textSecondary, alignSelf: "center" }}>
            Page {page + 1}
          </span>
          <button
            onClick={() => goPage(page + 1)}
            disabled={!hasMore}
            style={{
              padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500,
              fontFamily: "inherit", cursor: !hasMore ? "default" : "pointer",
              border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.surface, color: !hasMore ? COLORS.textMuted : COLORS.text,
              opacity: !hasMore ? 0.5 : 1,
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
